import type { Json } from "../../services/backend/database.types.js";
import type { AdminContentEditorValue } from "./adminContentEditor.js";
import { asObject, readAdminTravelEditor, updateAdminTravelEditor, validateAdminTravelEditor } from "./adminTravelEditor.js";
import type { Obj, Section, TravelEditorValue } from "./adminTravelEditor.js";
import { Button, Input, Select, Textarea } from "../../ui/components/Controls.js";
import { AdminEditorField } from "./AdminEditorField.js";
import { useAdminText } from "./adminI18n.js";

function text(value: Json | undefined): string {
    return typeof value === "string" || typeof value === "number" ? String(value) : "";
}

function AdminTravelEditorFields({ value, onChange }: {
    value: AdminContentEditorValue;
    onChange: (next: AdminContentEditorValue) => void;
}) {
    const adminText = useAdminText();
    const travel = readAdminTravelEditor(value);
    const errors = validateAdminTravelEditor(travel, value.contentKey);
    const change = (next: TravelEditorValue) => onChange(updateAdminTravelEditor(value, next));
    const updateCatalog = (field: string, next: Json) => change({
        ...travel, catalog: { ...travel.catalog, [field]: next }
    });
    const updateDocument = (field: string, next: Json) => change({
        ...travel, document: { ...travel.document, [field]: next }
    });
    const updateSection = (index: number, section: Section) => change({
        ...travel,
        sections: travel.sections.map((current, position) => position === index ? section : current)
    });

    return (
        <div className="space-y-5">
            {errors.length > 0 ? (
                <div role="alert" className="rounded-card border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    <strong>{adminText("À compléter avant publication")}</strong>
                    <ul className="mt-2 list-disc ps-5">{errors.map(error => <li key={error}>{adminText(error)}</li>)}</ul>
                </div>
            ) : <p className="rounded-card bg-emerald-50 p-3 text-sm text-emerald-800">{adminText("Leçon prête à être publiée.")}</p>}

            <fieldset className="grid gap-3 rounded-card border border-line p-4 sm:grid-cols-2">
                <legend className="px-2 font-bold">{adminText("Carte du catalogue")}</legend>
                <AdminEditorField label="Titre français" required>
                    <Input value={text(travel.catalog.title)} onChange={event => updateCatalog("title", event.target.value)} />
                </AdminEditorField>
                <AdminEditorField label="Titre persan">
                    <Input dir="rtl" value={text(travel.catalog.title_fa)} onChange={event => updateCatalog("title_fa", event.target.value)} />
                </AdminEditorField>
                <AdminEditorField label="Icône">
                    <Input value={text(travel.catalog.icon)} onChange={event => updateCatalog("icon", event.target.value)} />
                </AdminEditorField>
                <AdminEditorField label="Module">
                    <Input value={text(travel.catalog.module)} onChange={event => updateCatalog("module", event.target.value)} />
                </AdminEditorField>
                <AdminEditorField label="Ordre">
                    <Input type="number" min="0" value={text(travel.catalog.order)} onChange={event => updateCatalog("order", Number(event.target.value))} />
                </AdminEditorField>
                <AdminEditorField label="Durée estimée (minutes)">
                    <Input type="number" min="1" value={text(travel.catalog.estimatedTime)} onChange={event => updateCatalog("estimatedTime", Number(event.target.value))} />
                </AdminEditorField>
            </fieldset>

            <fieldset className="grid gap-3 rounded-card border border-line p-4 sm:grid-cols-2">
                <legend className="px-2 font-bold">{adminText("Leçon")}</legend>
                <AdminEditorField label="Titre français" required>
                    <Input value={text(travel.document.title)} onChange={event => updateDocument("title", event.target.value)} />
                </AdminEditorField>
                <AdminEditorField label="Titre persan">
                    <Input dir="rtl" value={text(travel.document.title_fa)} onChange={event => updateDocument("title_fa", event.target.value)} />
                </AdminEditorField>
                <AdminEditorField label="Icône">
                    <Input value={text(travel.document.icon)} onChange={event => updateDocument("icon", event.target.value)} />
                </AdminEditorField>
                <AdminEditorField label="Durée estimée (minutes)">
                    <Input type="number" min="1" value={text(travel.document.estimatedTime)} onChange={event => updateDocument("estimatedTime", Number(event.target.value))} />
                </AdminEditorField>
            </fieldset>

            <section className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                    <h3 className="font-bold">{adminText("Sections")} · {travel.sections.length}</h3>
                    <Button variant="secondary" onClick={() => change({
                        ...travel,
                        sections: [...travel.sections, {
                            id: `${value.contentKey || "voyage"}-${travel.sections.length + 1}`,
                            type: "lesson", title: "", title_fa: "", content: "", examples: []
                        }]
                    })}>+ {adminText("Ajouter une section")}</Button>
                </div>
                {travel.sections.map((section, index) => (
                    <div key={`${index}:${text(section.id)}`} className="space-y-3 rounded-card border border-line p-4">
                        <div className="flex flex-wrap justify-between gap-2">
                            <strong>{adminText("Section")} {index + 1}</strong>
                            <div className="flex gap-2">
                                <Button variant="ghost" disabled={index === 0} onClick={() => moveSection(travel, index, -1, change)}>↑</Button>
                                <Button variant="ghost" disabled={index === travel.sections.length - 1} onClick={() => moveSection(travel, index, 1, change)}>↓</Button>
                                <Button variant="ghost" onClick={() => change({ ...travel, sections: travel.sections.filter((_, i) => i !== index) })}>{adminText("Retirer")}</Button>
                            </div>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                            <AdminEditorField label="Identifiant stable" required>
                                <Input value={text(section.id)} onChange={event => updateSection(index, { ...section, id: event.target.value })} />
                            </AdminEditorField>
                            <AdminEditorField label="Type">
                                <Select value={text(section.type)} onChange={event => updateSection(index, {
                                    ...section, type: event.target.value,
                                    ...(event.target.value === "vocab" ? { words: section.words ?? [] } : {}),
                                    ...(event.target.value === "tips" ? { tips: section.tips ?? [] } : {}),
                                    ...(event.target.value === "exercise" ? { questions: section.questions ?? [] } : {})
                                })}>
                                    <option value="lesson">{adminText("Leçon")}</option><option value="vocab">{adminText("Vocabulaire")}</option>
                                    <option value="tips">{adminText("Conseils")}</option><option value="exercise">{adminText("Exercice")}</option>
                                </Select>
                            </AdminEditorField>
                            <AdminEditorField label="Titre français">
                                <Input value={text(section.title)} onChange={event => updateSection(index, { ...section, title: event.target.value })} />
                            </AdminEditorField>
                            <AdminEditorField label="Titre persan">
                                <Input dir="rtl" value={text(section.title_fa)} onChange={event => updateSection(index, { ...section, title_fa: event.target.value })} />
                            </AdminEditorField>
                        </div>
                        {section.type === "lesson" ? <>
                            <AdminEditorField label="Contenu"><Textarea value={text(section.content)} onChange={event => updateSection(index, { ...section, content: event.target.value })} /></AdminEditorField>
                            <Pairs title={adminText("Exemples")} rows={section.examples} fields={["fr", "fa"]} onChange={rows => updateSection(index, { ...section, examples: rows })} />
                        </> : null}
                        {section.type === "vocab" ? <Pairs title={adminText("Mots")} rows={section.words} fields={["fr", "fa", "phonetic", "emoji"]} onChange={rows => updateSection(index, { ...section, words: rows })} /> : null}
                        {section.type === "tips" ? <Pairs title={adminText("Conseils")} rows={section.tips} fields={["title_fr", "title_fa", "content_fr", "content_fa", "icon"]} onChange={rows => updateSection(index, { ...section, tips: rows })} /> : null}
                        {section.type === "exercise" ? <>
                            <AdminEditorField label="Questions affichées"><Input type="number" min="1" value={text(section.displayCount)} onChange={event => updateSection(index, { ...section, displayCount: Number(event.target.value) })} /></AdminEditorField>
                            <Pairs title={adminText("Questions")} rows={section.questions} fields={["type", "question", "options", "correct", "explanation"]} onChange={rows => updateSection(index, { ...section, questions: rows })} />
                        </> : null}
                        <div className="grid gap-3 sm:grid-cols-2">
                            <AdminEditorField label="Note"><Textarea value={text(section.note)} onChange={event => updateSection(index, { ...section, note: event.target.value })} /></AdminEditorField>
                            <AdminEditorField label="Note persane"><Textarea dir="rtl" value={text(section.note_fa)} onChange={event => updateSection(index, { ...section, note_fa: event.target.value })} /></AdminEditorField>
                        </div>
                    </div>
                ))}
            </section>
            <details className="rounded-card border border-line bg-line-soft p-4">
                <summary className="cursor-pointer font-bold">{adminText("JSON avancé")}</summary>
                <p className="mt-2 text-xs text-muted">{adminText("Les champs historiques non affichés sont conservés pendant l’édition structurée.")}</p>
                <Textarea className="mt-3 min-h-80 font-mono text-xs" value={value.payloadText} onChange={event => onChange({ ...value, payloadText: event.target.value })} />
            </details>
        </div>
    );
}

function moveSection(value: TravelEditorValue, index: number, direction: number, onChange: (next: TravelEditorValue) => void) {
    const sections = [...value.sections];
    const other = index + direction;
    [sections[index], sections[other]] = [sections[other], sections[index]];
    onChange({ ...value, sections });
}

function Pairs({ title, rows, fields, onChange }: {
    title: string;
    rows: Json | undefined;
    fields: string[];
    onChange: (rows: Obj[]) => void;
}) {
    const adminText = useAdminText();
    const values = Array.isArray(rows) ? rows.map(row => asObject(row)) : [];
    return <div className="space-y-2 rounded-control border border-line p-3">
        <div className="flex justify-between gap-2"><strong>{title} · {values.length}</strong>
            <Button variant="secondary" onClick={() => onChange([...values, Object.fromEntries(fields.map(field => [field, ""]))])}>+ {adminText("Ajouter")}</Button>
        </div>
        {values.map((row, index) => <div key={index} className="grid gap-2 border-t border-line pt-2 sm:grid-cols-2">
            {fields.map(field => <AdminEditorField key={field} label={field}>
                <Input value={Array.isArray(row[field]) ? (row[field] as Json[]).join(" | ") : text(row[field])}
                    onChange={event => onChange(values.map((candidate, position) => position === index
                        ? { ...candidate, [field]: field === "options" ? event.target.value.split("|").map(option => option.trim())
                            : field === "correct" ? Number(event.target.value) : event.target.value }
                        : candidate))} />
            </AdminEditorField>)}
            <Button variant="ghost" onClick={() => onChange(values.filter((_, position) => position !== index))}>{adminText("Retirer")}</Button>
        </div>)}
    </div>;
}

export { AdminTravelEditorFields };
